import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { format, isDate, startOfDay } from 'date-fns';
import { API_DATE_FORMAT } from 'lib/constants';
import React from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { FontFamilyRegular, theme } from 'lib/theme';
import { BUTTON_BORDER_RADIUS, BUTTON_HEIGHT } from 'components/Button';
import { TEXT_SIZE_NORMAL } from 'components/Txt';
import { formatDate } from 'lib/riskbase';

const DateFieldWidth = 120;

interface Props {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  min?: Date;
  max?: Date;
  clearable?: boolean;
  showClearButton?: boolean;
  placeholder?: string;
  style?: StyleProp<ViewStyle>;
  showFieldTypeIcon?: boolean;
  shrinkToWidth?: boolean;
}

export const DateField: React.FC<Props> = React.memo(
  ({
    label,
    onChange,
    placeholder,
    min,
    max,
    clearable = true,
    showClearButton = true,
    value,
    style,
    showFieldTypeIcon = true,
    shrinkToWidth = false,
  }) => {
    const handleChange = React.useCallback(
      (newValue: Date | '') => {
        let finalValue = newValue ? new Date(newValue) : '';
        if (isDate(finalValue)) {
          if (min && finalValue < startOfDay(min)) {
            finalValue = startOfDay(min);
          }
          if (max && finalValue > startOfDay(max)) {
            finalValue = startOfDay(max);
          }
          onChange(format(finalValue, API_DATE_FORMAT));
        } else if (clearable) {
          onChange('');
        }
      },
      [clearable, max, min, onChange],
    );

    const handleChangeEvent = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value: newValue } = event.target;
        handleChange(newValue ? new Date(newValue) : '');
      },
      [handleChange],
    );

    const shouldShowClearButton = value && clearable && showClearButton;

    return (
      <CardRow
        style={[
          {
            backgroundColor: theme.colors.white,
          },
          style,
        ]}
        contentStyle={[
          shrinkToWidth ? { flex: undefined } : undefined,
          {
            paddingVertical: 0,
            alignItems: 'flex-start',
          },
        ]}
        label={label}
        actionIcon={ICONS.CircleXmarkLight}
        onAction={shouldShowClearButton ? () => handleChange('') : undefined}
        navigationType={showFieldTypeIcon ? 'date-picker' : undefined}
      >
        <input
          type="date"
          onChange={handleChangeEvent}
          value={value}
          placeholder={placeholder}
          min={min ? formatDate(min, API_DATE_FORMAT) : undefined}
          max={max ? formatDate(max, API_DATE_FORMAT) : undefined}
          required={!clearable}
          style={{
            height: BUTTON_HEIGHT - 2,
            borderColor: theme.colors.blue,
            borderWidth: 1,
            borderStyle: 'solid',
            borderRadius: BUTTON_BORDER_RADIUS,
            paddingRight: 4,
            paddingLeft: 4,
            fontFamily: FontFamilyRegular,
            fontSize: TEXT_SIZE_NORMAL,
            width: DateFieldWidth - 10,
          }}
        />
      </CardRow>
    );
  },
);
